import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('cv_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      setIsAuthenticated(true);
    } catch (err) {
      localStorage.removeItem('cv_token');
      localStorage.removeItem('cv_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password, totpCode = null) => {
    if (totpCode) {
      const res = await authAPI.login2FA({ email, password, totp_code: totpCode });
      localStorage.setItem('cv_token', res.data.token);
      localStorage.setItem('cv_user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      setIsAuthenticated(true);
      return res.data;
    }
    
    const res = await authAPI.login({ email, password });
    
    if (res.data.requires_2fa) {
      return { requires_2fa: true, temp_token: res.data.temp_token };
    }
    
    localStorage.setItem('cv_token', res.data.token);
    localStorage.setItem('cv_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setIsAuthenticated(true);
    return res.data;
  };

  const register = async (email, password, name, referralCode = '') => {
    const res = await authAPI.register({ email, password, name, referral_code: referralCode });
    localStorage.setItem('cv_token', res.data.token);
    localStorage.setItem('cv_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setIsAuthenticated(true);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('cv_token');
    localStorage.removeItem('cv_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
